import React from 'react';
import Drawer from './Drawer';
import Button from './Button';
import FilterValueEditor from './FilterValueEditor';
import { Icon } from '../icons';
import { isFilterEmpty } from './filterModel';
import { useT } from '../../i18n';

/**
 * FilterDrawer — the "advanced" filtering surface for data-heavy tables.
 *
 * Shows every field stacked with its editor (room for many filters at once)
 * plus an optional list of saved views / segments. Use this alongside the
 * inline FilterBar: the bar handles quick edits, the drawer handles the heavy
 * cases and saved segments.
 *
 * Props:
 *   open, onClose
 *   fields, values, onChange(fieldId, value)
 *   savedViews: [{ id, label, description?, values }]
 *   onApplyView(view), onSaveCurrent()      — optional
 *   activeViewId: string                     — highlight the applied view
 */
const FilterDrawer = ({
  open,
  onClose,
  fields = [],
  values = {},
  onChange,
  savedViews = [],
  onApplyView,
  onSaveCurrent,
  activeViewId,
}) => {
  const t = useT();
  const activeCount = fields.filter((f) => !isFilterEmpty(f, values?.[f.id])).length;
  const clearAll = () => fields.forEach((f) => onChange?.(f.id, undefined));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      size="sm"
      title={t('Filters')}
      description={activeCount ? t('{count} active', { count: activeCount }) : t('Refine the table')}
      footer={
        <div className="ps-fdrawer__foot">
          <Button variant="ghost" size="sm" onClick={clearAll} disabled={!activeCount}>
            {t('Clear all')}
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>{t('Done')}</Button>
        </div>
      }
    >
      {savedViews.length > 0 && (
        <section className="ps-fdrawer__section">
          <div className="ps-fdrawer__section-head">
            <h3 className="ps-fdrawer__section-title">{t('Saved views')}</h3>
            {onSaveCurrent && (
              <button type="button" className="ps-fdrawer__save" onClick={onSaveCurrent} disabled={!activeCount}>
                <Icon.Plus size={13} /> {t('Save current')}
              </button>
            )}
          </div>
          <ul className="ps-fdrawer__views">
            {savedViews.map((view) => (
              <li key={view.id}>
                <button
                  type="button"
                  className={`ps-fdrawer__view${activeViewId === view.id ? ' is-active' : ''}`}
                  onClick={() => onApplyView?.(view)}
                >
                  <Icon.Bookmark size={15} className="ps-fdrawer__view-icon" />
                  <span className="ps-fdrawer__view-text">
                    <span className="ps-fdrawer__view-label">{view.label}</span>
                    {view.description && <span className="ps-fdrawer__view-desc">{view.description}</span>}
                  </span>
                  {activeViewId === view.id && <Icon.Check size={15} />}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="ps-fdrawer__section">
        <h3 className="ps-fdrawer__section-title">{t('All filters')}</h3>
        <div className="ps-fdrawer__fields">
          {fields.map((f) => {
            const FIcon = f.icon;
            const active = !isFilterEmpty(f, values?.[f.id]);
            return (
              <div key={f.id} className="ps-fdrawer__field">
                <div className="ps-fdrawer__field-head">
                  {FIcon && <FIcon size={15} className="ps-fdrawer__field-icon" />}
                  <span className="ps-fdrawer__field-label">{f.label}</span>
                  {active && (
                    <button type="button" className="ps-fdrawer__field-clear" onClick={() => onChange?.(f.id, undefined)}>
                      {t('Clear')}
                    </button>
                  )}
                </div>
                <FilterValueEditor field={f} value={values?.[f.id]} onChange={(val) => onChange?.(f.id, val)} />
              </div>
            );
          })}
        </div>
      </section>
    </Drawer>
  );
};

export default FilterDrawer;
